import { CoralogixRum, CoralogixLogSeverity } from '@coralogix/browser';
import type { CoralogixBrowserSdkConfig } from '@coralogix/browser';
import type { BootData } from '@/types/boot';
import { getEnvVariable } from '@/utils/get-env-variable';

const CORALOGIX_RUM_SAMPLING_RATE = 50;

let isRumInitialized = false;

export const initCoralogixRum = (customerEmail?: string, bootData?: BootData): void => {
  const publicKey = getEnvVariable('VITE_CORALOGIX_RUM_PUBLIC_KEY');

  if (!publicKey) {
    console.warn('Coralogix RUM public key not found. RUM monitoring will be disabled.');
    return;
  }

  if (isRumInitialized) {
    console.warn('Coralogix RUM is already initialized.');
    return;
  }

  const publisherId = bootData?.checkoutTheme?.publisherId || bootData?.theme?.publisherId || 'unknown';

  const config: CoralogixBrowserSdkConfig = {
    public_key: publicKey,
    stringifyCustomLogData: true,
    application: 'checkout-v3',
    version: '1.0.0',
    coralogixDomain: getEnvVariable('VITE_CORALOGIX_DOMAIN', 'US2'),
    sessionConfig: {
      onlyWithErrorConfig: {
        enable: true,
      },
      sessionSampleRate: CORALOGIX_RUM_SAMPLING_RATE,
      alwaysTrackSessionsWithErrors: true,
    },
    user_context: {
      user_id: '',
      user_email: customerEmail || 'anonymous@example.com',
      user_name: '',
    },
    labels: {
      publisherId: publisherId,
      publisherName: bootData?.theme?.publisherName || 'unknown',
      environment: getEnvVariable('VITE_ENV', 'production'),
      platform: 'web',
    },
  };

  try {
    CoralogixRum.init(config);
    isRumInitialized = true;
  } catch (error) {
    console.error('Failed to initialize Coralogix RUM:', error);
  }
};

export const setRumUserContext = (userContext: { user_email: string; user_name: string; user_id: string }): void => {
  if (!isRumInitialized) {
    return;
  }

  try {
    CoralogixRum.setUserContext(userContext);
  } catch (error) {
    console.error('Failed to set RUM user context:', error);
  }
};

export const updateRumCustomerData = (sessionToken: string, customerEmail?: string): void => {
  if (!isRumInitialized) {
    return;
  }

  setRumUserContext({
    user_id: sessionToken,
    user_email: customerEmail || 'anonymous@example.com',
    user_name: '',
  });
};

export const logToCoralogix = (
  level: CoralogixLogSeverity,
  message: string,
  data?: Record<string, unknown> | null,
  labels?: Record<string, string | number | boolean>
): void => {
  if (!isRumInitialized) {
    return;
  }

  try {
    CoralogixRum.log(level, message, data || undefined, labels);
  } catch (error) {
    console.error('Failed to send log to Coralogix:', error);
  }
};

export const logDebug = (
  message: string,
  data?: Record<string, unknown> | null,
  labels?: Record<string, string | number | boolean>
): void => {
  logToCoralogix(CoralogixLogSeverity.Debug, message, data, labels);
};

export const logInfo = (
  message: string,
  data?: Record<string, unknown> | null,
  labels?: Record<string, string | number | boolean>
): void => {
  logToCoralogix(CoralogixLogSeverity.Info, message, data, labels);
};

export const logWarn = (
  message: string,
  data?: Record<string, unknown> | null,
  labels?: Record<string, string | number | boolean>
): void => {
  logToCoralogix(CoralogixLogSeverity.Warn, message, data, labels);
};

export const logError = (
  message: string,
  errorData?: Error | unknown,
  additionalData?: Record<string, unknown>,
  labels?: Record<string, string | number | boolean>
): void => {
  const error = errorData instanceof Error ? errorData : null;
  const data = {
    ...additionalData,
    error: errorData
      ? {
          name: error?.name || 'Error',
          message: error?.message || String(errorData),
          stack: error?.stack,
        }
      : undefined,
  };

  logToCoralogix(CoralogixLogSeverity.Error, message, data, labels);
};

export const logCritical = (
  message: string,
  data?: Record<string, unknown> | null,
  labels?: Record<string, string | number | boolean>
): void => {
  logToCoralogix(CoralogixLogSeverity.Critical, message, data, labels);
};

export const reportError = (message: string, data?: Record<string, unknown> | null): void => {
  if (!isRumInitialized) {
    return;
  }

  try {
    CoralogixRum.error(message, data || undefined);
  } catch (err) {
    console.error('Failed to report error to Coralogix:', err);
  }
};

export const setCoralogixLabels = (labels: Record<string, string | number | boolean>): void => {
  if (!isRumInitialized) {
    return;
  }

  try {
    CoralogixRum.setLabels(labels);
  } catch (error) {
    console.error('Failed to set Coralogix labels:', error);
  }
};

export default {
  initCoralogixRum,
  setRumUserContext,
  updateRumCustomerData,
  logToCoralogix,
  logInfo,
  logWarn,
  logError,
  logDebug,
  logCritical,
  reportError,
  setCoralogixLabels,
};
